import { rgba } from 'polished';
import {
	PropsWithChildren,
	ReactNode,
	cloneElement,
	isValidElement,
	useEffect,
	useState,
} from 'react';
import { Link, Route, useRouteMatch } from 'react-router-dom';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChevronRightIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { Avatar } from './Avatar';
import { Highlight } from './Highlight';
import { Skeleton } from './Skeleton';
import { Spinner } from './Spinner';
import { Label, Paragraph } from './Text';

const Container = styled(Box)`
	background: ${tokens.surface.secondary};
	height: 100%;
`;

const Contents = styled(Box)<{ sticky?: boolean; scrollable?: boolean }>`
	position: ${(props) => (props.sticky ? 'sticky' : 'relative')};
	top: 0;
	height: auto;

	${(props) =>
		props.scrollable &&
		`
		max-height: 100vh;
		overflow-y: auto;
	`}
`;

const Icon = styled(Box)``;

const Badge = styled(Box)``;

const Subtitle = styled(Label)``;

type SidebarStatus = 'active' | 'inactive';

const SidebarItemIndicator = styled(Box)<{ status?: SidebarStatus }>`
	width: 10px;
	height: 10px;
	border-radius: 5px;
	position: absolute;
	right: 14px;

	${(props) =>
		props.status &&
		`
			background-color: ${props.status === 'active' ? tokens.success.icon : tokens.border.default};
			display: block;
		`}
`;

const SidebarItemContainer = styled(Flex)<{
	hover?: boolean;
	selected?: boolean;
	small?: boolean;
	overflow?: 'hidden';
	$disabled?: boolean;
	status?: SidebarStatus;
	secondary?: boolean;
}>`
	${(props) =>
		`
		border-radius: ${tokens.radius.sm}px;
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: 500;
	min-height: 36px;
	align-items: center;
	cursor: ${!props.$disabled ? 'pointer' : 'inherit'};
	color: ${tokens.text.secondary};
	padding: ${tokens.padding.compact}px ${tokens.padding.control}px;
	border: 1px solid transparent;
	transition: 0.1s ease-out all;
	position: relative;
	&& {
		// prevent reflexbox from overriding in storybook by using &&
		margin-bottom: 2px;
	}

	${props.overflow === 'hidden' && `white-space: nowrap;`}
	${props.small && `;min-height: 32px; padding: 6px ${tokens.padding.control}px;`}
	
		${
			props.overflow === 'hidden' &&
			`
			white-space: nowrap;
			overflow: hidden;
		`
		}
		${Subtitle} {
		color: ${tokens.text.tertiary};
	}

	${Icon} svg {
		width: 20px;
		height: 20px;
		display: block;
	}

		${
			props.selected
				? `
			background: ${!props.$disabled ? tokens.surface.white : 'transparent'};
			border-color: ${!props.$disabled ? tokens.border.subtle : 'transparent'};
			box-shadow: ${!props.$disabled ? tokens.shadow.button : 'none'};
			color: ${tokens.text.primary};
			font-weight: ${tokens.typography.weights.medium};

			${Icon} {
				color: ${props.theme.colors.primary};
			}

			${Subtitle} {
				color: ${tokens.text.secondary};
				font-weight: normal;
			}
		`
				: `
			${Icon} {
				color: ${tokens.icon.secondary};
			}

			${
				props.hover === true && !props.$disabled
					? `
				background: ${tokens.surface.primary};
				border-color: ${tokens.border.subtle};
				color: ${tokens.text.primary};

				${Icon} {
					color: ${tokens.icon.primary};
				}
			`
					: ''
			}
			${
				props.hover === undefined && !props.$disabled
					? `&:hover {
					background: ${tokens.surface.primary};
					border-color: ${tokens.border.subtle};
					color: ${tokens.text.primary};

					${Icon} {
						color: ${tokens.icon.primary};
					}
				}`
					: ''
			}
		`
		}
	`}
`;

const DROPDOWN_CHEVRON = 12;

const Rotation = styled(Box)<{ rotate: string }>`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	transform: rotate(${(props) => props.rotate});
	transition: transform 200ms ease-out;

	svg {
		width: ${DROPDOWN_CHEVRON}px;
		height: ${DROPDOWN_CHEVRON}px;
		display: block;
		color: ${tokens.icon.secondary};
	}
`;

const ChevronContainer = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;

	svg {
		width: ${DROPDOWN_CHEVRON}px;
		height: ${DROPDOWN_CHEVRON}px;
		display: block;
		color: ${tokens.icon.secondary};
	}
`;

const TitleContainer = styled(Box)`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

function renderSidebarIcon(icon: ReactNode, selected: boolean, loading?: boolean) {
	if (loading) {
		return <Spinner />;
	}
	if (!isValidElement(icon)) {
		return icon;
	}
	return cloneElement(icon, { solid: selected } as { solid: boolean });
}

export function Sidebar(
	props: PropsWithChildren<BoxProps> & {
		sticky?: boolean;
		scrollable?: boolean;
		p?: number;
		onMouseEnter?: () => void;
		onMouseLeave?: () => void;
	},
) {
	return (
		<Container {...props}>
			<Contents sticky={props.sticky} scrollable={props.scrollable} p={props.p ?? 3}>
				{props.children}
			</Contents>
		</Container>
	);
}

Sidebar.Custom = function SidebarItem(props: PropsWithChildren<{ small?: boolean }>) {
	return (
		<Flex px={props.small ? '12px' : 3} height={props.small ? 40 : 50} alignItems="center">
			{props.children}
		</Flex>
	);
};

Sidebar.Section = function SidebarSection(
	props: PropsWithChildren<{ title?: string | ReactNode }>,
) {
	return (
		<Box pt={3}>
			{props.title && typeof props.title === 'string' ? (
				<Sidebar.SectionTitle mb={1}>{props.title}</Sidebar.SectionTitle>
			) : (
				props.title
			)}
			{props.children}
		</Box>
	);
};

Sidebar.Item = Skeleton(
	function SidebarItem(
		props: BoxProps & {
			hover?: boolean;
			icon?: React.ReactNode;
			badge?: string | number;
			title?: ReactNode;
			subtitle?: string;
			exact?: boolean;
			small?: boolean;
			loading?: boolean;
			children?: ((props: { selected: boolean }) => ReactNode) | ReactNode | ReactNode[];
			status?: SidebarStatus;
			onMouseEnter?: () => void;
			onMouseLeave?: () => void;
			overflow?: 'hidden';
			selected?: boolean;
			disabled?: boolean;
			secondary?: boolean;
		} & (
				| {
						path: string[] | string;
						target?: string;
				  }
				| {
						onClick: () => Promise<unknown> | void;
				  }
			),
	) {
		if ('onClick' in props) {
			return (
				<SidebarItemContainer
					{...props}
					hover={props.hover}
					small={props.small}
					status={props.status}
					overflow={props.overflow}
					selected={props.selected}
					$disabled={props.disabled}
				>
					<Flex justifyContent="space-between" alignItems="center" width="100%">
						<Flex alignItems="center" overflow="hidden">
							{props.icon && (
								<Icon mr={2} flexShrink={0} flexGrow={0}>
									{renderSidebarIcon(props.icon, !!props.selected, props.loading)}
								</Icon>
							)}
							<TitleContainer>
								{props.title}
								{props.subtitle && <Subtitle>{props.subtitle}</Subtitle>}
							</TitleContainer>
							{typeof props.children === 'function'
								? props.children({ selected: false })
								: props.children}
							<SidebarItemIndicator status={props.status} />
						</Flex>
						{props.badge && (
							<Badge fontWeight={700} ml={2} opacity={props.badge === '—' ? 0.3 : 1.0}>
								{props.badge}
							</Badge>
						)}
					</Flex>
				</SidebarItemContainer>
			);
		}

		return (
			<Link to={Array.isArray(props.path) ? props.path[0] : props.path} target={props.target}>
				<Route path={props.path} exact={props.exact}>
					{({ match }) => (
						<SidebarItemContainer
							{...props}
							hover={props.hover}
							selected={!!match || props.selected}
							small={props.small}
							overflow={props.overflow}
							$disabled={props.disabled}
						>
							<Flex justifyContent="space-between" alignItems="center" width="100%">
								<Flex alignItems="center" overflow="hidden" width="100%">
									{props.icon && (
										<Icon mr={2} flexShrink={0} flexGrow={0}>
											{renderSidebarIcon(props.icon, !!match || !!props.selected, props.loading)}
										</Icon>
									)}
									<TitleContainer>
										{props.title}
										{props.subtitle && <Subtitle>{props.subtitle}</Subtitle>}
									</TitleContainer>
									{typeof props.children === 'function'
										? props.children({ selected: !!match })
										: props.children}
									<SidebarItemIndicator status={props.status} />
								</Flex>
								{props.badge && (
									<Badge fontWeight={700} ml={2} opacity={props.badge === '—' ? 0.3 : 1.0}>
										{props.badge}
									</Badge>
								)}
							</Flex>
						</SidebarItemContainer>
					)}
				</Route>
			</Link>
		);
	},
	function (props: { small?: boolean }) {
		return (
			<Box p={props.small ? '8px 12px' : 3} mb={1}>
				<Flex>
					<Skeleton.Box height={24} width={24} mr={2} />
					<Skeleton.Box height={24} flexGrow={1} />
				</Flex>
			</Box>
		);
	},
);

Sidebar.ItemDropdown = Skeleton(
	function SideBarItemDropdown(
		props: PropsWithChildren<{
			title: string;
			isOpen?: boolean;
		}>,
	) {
		const [isOpenValue, setIsOpenValue] = useState(props.isOpen ?? false);

		useEffect(() => {
			if (props.isOpen) {
				setIsOpenValue(true);
			}
		}, [props.isOpen]);

		return (
			<>
				<Box
					onClick={() => setIsOpenValue(!isOpenValue)}
					style={{ cursor: 'pointer' }}
					pt={`${tokens.padding.compact}px`}
					pb={`${tokens.gap.inline}px`}
				>
					<Flex alignItems="center">
						{isOpenValue !== undefined && (
							<Rotation
								width={DROPDOWN_CHEVRON}
								mr={`${tokens.gap.inline}px`}
								rotate={isOpenValue ? '90deg' : '0deg'}
							>
								<ChevronRightIcon size={DROPDOWN_CHEVRON} />
							</Rotation>
						)}
						<Label width="100%">
							<Flex width="100%" justifyContent="space-between" alignItems="center">
								{props.title}
							</Flex>
						</Label>
					</Flex>
				</Box>

				{isOpenValue && props.children}
			</>
		);
	},
	function () {
		return (
			<Box py={`${tokens.padding.compact}px`}>
				<Flex alignItems="center">
					<ChevronContainer mr={`${tokens.gap.inline}px`}>
						<ChevronRightIcon size={DROPDOWN_CHEVRON} />
					</ChevronContainer>
					<Skeleton.Box height={18} flexGrow={1} />
				</Flex>
			</Box>
		);
	},
);

Sidebar.Spacing = styled(Box)<{ small?: boolean }>`
	margin-top: ${(props) => (props.small ? 8 : 16)}px;
	margin-bottom: ${(props) => (props.small ? 8 : 16)}px;
	width: 100%;
`;

Sidebar.Separator = styled(Box)`
	margin: 16px -16px;
	height: 1px;
	background: ${tokens.border.subtle};
`;

Sidebar.SectionTitle = styled(Paragraph)`
	font-weight: bold;
	font-size: 14px;
	padding-left: 14px;
	flex-shrink: 0;
`;

const UserContainer = styled(Link)<{ selected?: boolean }>`
	display: block;
	padding: 0px 2px;
	margin: 0px -2px;
	border-radius: 4px;

	${(props) =>
		props.selected
			? `
		background: ${rgba(props.theme.colors.primary, 0.1)};

        ${Paragraph} {
            color: ${props.theme.colors.primary};
            font-weight: bold;
        }
	`
			: `
        &:hover {
            background: ${rgba(tokens.border.subtle, 0.5)};
        }
    `}
`;

function User(props: {
	to: string;
	user: { id: string; firstName: string; lastName: string };
	query: string;
}) {
	const match = !!useRouteMatch(props.to);

	return (
		<UserContainer selected={match} to={props.to}>
			<Flex py={1} alignItems="center">
				<Box flexGrow={0} flexShrink={0} mr={2}>
					<Avatar user={props.user} size={24} />
				</Box>
				<Paragraph flexGrow={1} flexShrink={1}>
					<Highlight query={props.query}>
						{props.user.firstName} {props.user.lastName}
					</Highlight>
				</Paragraph>
			</Flex>
		</UserContainer>
	);
}

function UserSkeleton() {
	return (
		<Flex py={1} alignItems="center">
			<Box flexGrow={0} flexShrink={0} mr={2}>
				<Avatar.Skeleton size={24} />
			</Box>
			<Paragraph.Skeleton lines={1} />
		</Flex>
	);
}

Sidebar.User = User;
User.Skeleton = UserSkeleton;
